import React from 'react'
import PropTypes from 'prop-types'
import Text from '../../ui/Text'
import Tooltip from '../../ui/Tooltip'

// Snake_case keys come straight from the Rails payload; access via constants to
// Satisfy the camelcase/dot-notation lint rules.
const SECTION_ID = 'section_id'
const SECTION_NAME = 'section_name'

const WRAPPER_CLASS = 'tw:flex tw:flex-col tw:gap-6'
const SECTION_CLASS = 'tw:flex! tw:flex-col tw:gap-2'
const TITLE_CLASS = 'tw:text-base! tw:font-medium! tw:text-gray-900! tw:m-0! tw:min-w-0 tw:break-words'
const TABLE_CLASS = 'tw:w-full tw:table-fixed tw:border-collapse tw:text-sm'
const COL_CLASS = 'tw:w-1/2'
const TH_CLASS = 'tw:text-left tw:font-medium tw:text-gray-600 tw:bg-gray-50 tw:p-3 '
  + 'tw:border tw:border-solid tw:border-gray-200'
const TD_CLASS = 'tw:p-3 tw:border tw:border-solid tw:border-gray-200 tw:align-top'
const QUESTION_TEXT_CLASS = 'tw:text-gray-700 tw:break-words'
const VALUE_TEXT_CLASS = 'tw:text-gray-900 tw:break-words'
const PHOTOS_CLASS = 'tw:flex tw:flex-wrap tw:gap-2'
const PHOTO_LINK_CLASS = 'tw:block tw:w-16 tw:h-16'
const PHOTO_IMG_CLASS = 'tw:w-16 tw:h-16 tw:object-cover tw:border tw:border-solid '
  + 'tw:border-gray-200 tw:rounded-md! tw:cursor-pointer'
const SIGNATURE_IMG_CLASS = 'tw:max-w-48 tw:max-h-24 tw:border tw:border-solid '
  + 'tw:border-gray-200 tw:rounded-md!'

const EMPTY_CLASS = 'tw:text-sm tw:text-gray-500 tw:py-8 tw:text-center'

const THUMBNAIL_URL_KEY = 'thumbnail_url'
const SIGNATURE_URL_KEY = 'signature_url'
const EMPTY_VALUE = '--'
const EMPTY_RESULTS = []
const EMPTY_PHOTOS = []

const GRADE_LABELS_KEY = 'grade_labels'
const EMPTY_GRADE_LABELS = []
const GRADE_SCALE_CLASS = 'tw:flex tw:flex-col tw:gap-1 tw:text-left'
const GRADE_ROW_CLASS = 'tw:flex tw:items-center tw:gap-1'
const GRADE_LABEL_CLASS = 'tw:font-medium'
const GRADE_VALUE_CLASS = 'tw:inline-flex tw:items-center tw:gap-1'
const GRADE_ICON_CLASS = 'fas fa-exclamation-circle tw:text-gray-800 tw:text-xs tw:shrink-0'

const renderGradeScale = labels => (
  <span className={GRADE_SCALE_CLASS}>
    {labels.map(item => (
      <span key={item.grade} className={GRADE_ROW_CLASS}>
        <span className={GRADE_LABEL_CLASS}>{item.label}</span>
        <span aria-hidden="true">→</span>
        <span>{`Note de ${item.grade}`}</span>
      </span>
    ))}
  </span>
)

const renderAnswer = question => {
  const photos = question.photos || EMPTY_PHOTOS
  if (photos.length > 0) {
    return (
      <div className={PHOTOS_CLASS}>
        {photos.map(photo => (
          <a key={photo.url} className={PHOTO_LINK_CLASS} href={photo.url} rel="noreferrer" target="_blank">
            <img alt="Photo de la réponse" className={PHOTO_IMG_CLASS} src={photo[THUMBNAIL_URL_KEY]} />
          </a>
        ))}
      </div>
    )
  }
  if (question[SIGNATURE_URL_KEY])
    return <img alt="Signature" className={SIGNATURE_IMG_CLASS} src={question[SIGNATURE_URL_KEY]} />
  const gradeLabels = question[GRADE_LABELS_KEY] || EMPTY_GRADE_LABELS
  if (gradeLabels.length > 0 && question.value) {
    return (
      <span className={GRADE_VALUE_CLASS}>
        <span className={VALUE_TEXT_CLASS}>{question.value}</span>
        <Tooltip content={renderGradeScale(gradeLabels)}>
          <i aria-hidden="true" className={GRADE_ICON_CLASS} />
        </Tooltip>
      </span>
    )
  }
  if ((question.value ?? '') === '')
    return <span className="tw:text-gray-500">{EMPTY_VALUE}</span>
  return (
    <Text className={VALUE_TEXT_CLASS} lines={2} title={question.value}>
      {question.value}
    </Text>
  )
}

const IssueResultsTab = ({ results = EMPTY_RESULTS }) => {
  if (results.length === 0)
    return <p className={EMPTY_CLASS}>Aucun résultat à afficher pour cette ressource.</p>
  return (
    <div className={WRAPPER_CLASS}>
      {results.map(section => (
        <section key={section[SECTION_ID]} className={SECTION_CLASS}>
          <h3 className={TITLE_CLASS}>{section[SECTION_NAME]}</h3>
          <table className={TABLE_CLASS}>
            <colgroup>
              <col className={COL_CLASS} />
              <col className={COL_CLASS} />
            </colgroup>
            <thead>
              <tr>
                <th className={TH_CLASS}>Question</th>
                <th className={TH_CLASS}>Réponse</th>
              </tr>
            </thead>
            <tbody>
              {section.questions.map(question => (
                <tr key={question.id}>
                  <td className={TD_CLASS}>
                    <Text className={QUESTION_TEXT_CLASS} lines={2} title={question.title}>
                      {question.title}
                    </Text>
                  </td>
                  <td className={TD_CLASS}>
                    {renderAnswer(question)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  )
}

IssueResultsTab.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape({
    questions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string,
      value: PropTypes.string,
      [SIGNATURE_URL_KEY]: PropTypes.string,
      [GRADE_LABELS_KEY]: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        grade: PropTypes.number
      })),
      photos: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string.isRequired,
        [THUMBNAIL_URL_KEY]: PropTypes.string.isRequired
      }))
    }))
  }))
}

IssueResultsTab.defaultProps = {
  results: EMPTY_RESULTS
}

export default IssueResultsTab
